import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown'
import { faCaretUp } from '@fortawesome/free-solid-svg-icons/faCaretUp'
import styled, { css } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { ReactElement, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import displayDateTime from '../../lib/displayDateTime'
import Text from '../Text'
import ButtonGroupPagination from '../common/ButtonGroupPagination'
import { ChargersPagination } from '../../lib/chargers-pagination'
import { compareSemanticVersions, getFirmwareVersion } from '../../common/services/utilsService'
import useText from '../../common/hooks/useText'
import { ChargePointStatus } from '../../common/enums/ChargePointStatusEnum'
import { ChargePoint } from '../../common/redux/api/web-api'

import FWStatusIcon from './FWStatusIcon'
import canUpdateChargePoint from './canUpdateChargePoint'
import { SStatusIcon } from './updateChargersFirmwareFilters'

const WidgetWrapper = styled.div`
    margin-top: 40px;
    padding-top: 40px;
    border-top: 1px solid #b3b3b3;
`

export const SWidgetHeader = styled.div`
    text-transform: capitalize;
    font-family: 'Lateral Extended';
    font-weight: 500;
    font-size: 1.25rem;
    margin-bottom: 20px;
`

const SpinnerContainer = styled.div`
    text-align: center;
`

const SCheckboxLabel = styled.label`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2em;
`

const SSearchResultsNumber = styled.div`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
`

const SFilterGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 0 -15px;
`

const SFilter = styled.div<{ actions?: boolean; large?: boolean }>`
    padding: 0 15px 1em;

    ${(p) =>
        p.actions
            ? css`
                  @media (min-width: 1024px) {
                      margin-left: auto;
                      display: flex;
                      justify-content: flex-end;
                      align-items: flex-end;
                  }
              `
            : ''}

    flex: 1 0 auto;
    ${(p) =>
        p.large
            ? css`
                  @media (max-width: 1023px) {
                      width: 100%;
                  }
              `
            : ''}

    @media (max-width: 767px) {
        width: 100%;
    }
`

const STableWrapper = styled.div`
    width: 100%;
    overflow: auto;
`

const STable = styled.table`
    min-width: 100%;
`

type TableColumnProps = React.ThHTMLAttributes<HTMLTableHeaderCellElement> & {
    children: React.ReactNode
    sorted?: 'asc' | 'desc'
}

const TableColumn = ({ children, sorted, ...props }: TableColumnProps) => (
    <th {...props}>
        {children} {sorted && <FontAwesomeIcon icon={sorted === 'asc' ? faCaretDown : faCaretUp} />}
    </th>
)

const STableColumn = styled(TableColumn)<{ sortable?: boolean; width?: string }>`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 12px;
    font-weight: 300;
    line-height: 18px;
    padding: 12px 8px;
    white-space: nowrap;
    ${(p) =>
        p.sortable &&
        css`
            cursor: pointer;
            color: ${({ theme }) => theme.brandColors.CA_Blue};
        `}
    width: ${({ width }) => width ?? 'inherit'};
`

const STableRow = styled.tr`
    &:nth-child(odd) {
        background: #f5f5f5;
    }
`

const STableCell = styled.td`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 14px;
    font-weight: 300;
    line-height: 24px;
    padding: 12px 8px;
`

const columns = [
    { name: 'Id', sortableProperty: 'id' },
    { name: 'Name', sortableProperty: 'name' },
    { name: 'Status', sortableProperty: 'chargePointStatus' },
    { name: 'Organisation', sortableProperty: 'companyName' },
    { name: 'Current FW', sortableProperty: 'installedFirmwareVersion' },
    { name: 'Update to' },
    { name: 'FW update status' },
    { name: 'Last updated', sortableProperty: 'firmwareLastUpdated' },
]

export interface UpdateChargerFirmwareModel {
    firmwareVersion?: number
    chargePointIds: Array<string>
}

type UpdateChargersFirmwareListProps = {
    filters: FirmwareGetChargePointsByFirmwareTypeApiArg
    sortBy?: string
    currentPage: number
    latestFirmwareVersion: number
    handlePageClick: (selected: number) => void
    sortDirection?: 'asc' | 'desc'
    onChangeSort: (sortBy: string, sortDirection: 'asc' | 'desc') => void
    onSelect: (id: string, checked: boolean) => void
    onSelectAll: (checked: boolean) => void
    onUpdate: (updateModel?: UpdateChargerFirmwareModel) => void
    chargePoints: IChargePoint[]
    totalCount: number
    setCurrentPage: (currentPage: number) => void
    isPending: boolean
    setSelectedPageSize: (pageSize: number) => void
}

const UpdateChargersFirmwareList = ({
    filters,
    totalCount,
    currentPage,
    chargePoints,
    handlePageClick,
    latestFirmwareVersion,
    sortBy,
    sortDirection,
    onSelect,
    onSelectAll,
    setCurrentPage,
    onChangeSort,
    onUpdate,
    setSelectedPageSize,
    isPending,
}: UpdateChargersFirmwareListProps): ReactElement => {
    const text = useText()
    const [selectAllCheckboxIsDisabled, setSelectAllCheckboxIsDisabled] = useState(false)
    const [pageSize, setPageSize] = useState(50)
    const sortedChargePoints = useMemo(() => {
        if (!sortBy || !sortDirection) return chargePoints
        return [...chargePoints].sort((a, b) => {
            const directionMultiplier = sortDirection === 'asc' ? 1 : -1

            if (typeof a[sortBy] !== 'string') {
                if (a[sortBy] < b[sortBy]) return directionMultiplier * -1
                if (a[sortBy] > b[sortBy]) return directionMultiplier * 1
                return 0
            }

            if (a[sortBy] !== '' && !b[sortBy]) return directionMultiplier * 1
            if (b[sortBy] !== '' && !a[sortBy]) return directionMultiplier * -1

            const firstItem = a[sortBy].toString().toLowerCase()
            const secondItem = b[sortBy].toString().toLowerCase()

            if (firstItem < secondItem) return directionMultiplier * -1
            if (firstItem > secondItem) return directionMultiplier * 1
            return 0
        })
    }, [chargePoints, sortBy, sortDirection])

    const sort = (name: string) => onChangeSort(name, (name === sortBy && sortDirection === 'asc' && 'desc') || 'asc')

    const allChecked = useMemo(() => {
        const editableChargePoints = chargePoints.filter((chargePoint) => {
            return (
                canUpdateChargePoint(chargePoint) &&
                (latestFirmwareVersion !== +getFirmwareVersion(chargePoint.ocppConfigKeys) ||
                    compareSemanticVersions(latestFirmwareVersion, getFirmwareVersion(chargePoint.ocppConfigKeys)))
            )
        })
        setSelectAllCheckboxIsDisabled(editableChargePoints.length === 0)
        return editableChargePoints.length > 0 ? editableChargePoints.every((cp) => cp.checked) : false
    }, [chargePoints, latestFirmwareVersion])

    const getStatus = (chargePoint: ChargePoint) => {
        if (filters.status === ChargePointStatus.Charging && chargePoint.isCharging) return ChargePointStatus.Charging
        if (chargePoint.chargePointStatus === ChargePointStatus.Online) return ChargePointStatus.Online
        return chargePoint.chargePointStatus
    }

    return (
        <WidgetWrapper>
            <div>
                <SWidgetHeader>
                    <Text code="lblChargersWidget"></Text>
                    {isPending && <span className="fa fa-spinner fa-pulse margin-left-small"></span>}
                </SWidgetHeader>
                <SSearchResultsNumber data-testid="search-result">
                    {!isPending && (totalCount > 0 ? `${totalCount} chargers found` : text('lblNoChargersFound'))}
                </SSearchResultsNumber>
                <SFilterGroup>
                    {totalCount > 20 && !isPending && (
                        <>
                            <div className="text-right">
                                <div data-testid="pagination-title">
                                    <Text code="msgResultPage"></Text>
                                    <span>:</span>
                                </div>
                                <ButtonGroupPagination
                                    value={pageSize}
                                    onChange={(value: number) => {
                                        setPageSize(value)
                                        setSelectedPageSize(value)
                                        setCurrentPage(0)
                                    }}
                                    dataTestId="button-paginate-group"
                                ></ButtonGroupPagination>
                            </div>
                        </>
                    )}

                    {totalCount > 0 && (
                        <SFilter actions large>
                            <button
                                disabled={!chargePoints.some((cp) => cp.checked)}
                                type="button"
                                className="btn btn-ok btn-sm heading5"
                                onClick={() => onUpdate()}
                                data-testid="btn-update"
                            >
                                <Text code="btnUpdate" />
                            </button>
                        </SFilter>
                    )}
                </SFilterGroup>
            </div>
            {isPending && (
                <SpinnerContainer data-testid="loader">
                    <div className="shimmer-container shimmer-medium" />
                </SpinnerContainer>
            )}
            {!!sortedChargePoints.length && !isPending && (
                <STableWrapper>
                    <STable className="table">
                        <thead>
                            <tr>
                                <STableColumn width="5%">
                                    <SCheckboxLabel data-testid="checkbox-select-all">
                                        <input
                                            className="checkbox-black"
                                            type="checkbox"
                                            onChange={(event) => onSelectAll(event.target.checked)}
                                            checked={allChecked}
                                            disabled={selectAllCheckboxIsDisabled}
                                            title="Select All"
                                            data-testid="checkbox-select-all"
                                        />
                                    </SCheckboxLabel>
                                </STableColumn>
                                {columns.map(({ name, sortableProperty }) => (
                                    <STableColumn
                                        data-testid={'col' + name}
                                        key={name}
                                        sortable={!!sortableProperty}
                                        sorted={sortBy === sortableProperty ? sortDirection : undefined}
                                        onClick={() => sortableProperty && sort(sortableProperty)}
                                    >
                                        {name}
                                    </STableColumn>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedChargePoints?.map((chargePoint) => {
                                const checkBoxIsDisabled =
                                    !canUpdateChargePoint(chargePoint) ||
                                    latestFirmwareVersion === +getFirmwareVersion(chargePoint.ocppConfigKeys) ||
                                    !compareSemanticVersions(
                                        latestFirmwareVersion,
                                        getFirmwareVersion(chargePoint.ocppConfigKeys)
                                    )

                                return (
                                    <STableRow key={chargePoint.id}>
                                        <STableCell>
                                            <SCheckboxLabel>
                                                <input
                                                    className="checkbox-black"
                                                    type="checkbox"
                                                    title={
                                                        checkBoxIsDisabled
                                                            ? 'ChargePoint is offline or already has the latest firmware version'
                                                            : undefined
                                                    }
                                                    checked={!!chargePoint.checked}
                                                    onChange={(event) => onSelect(chargePoint.id, event.target.checked)}
                                                    disabled={checkBoxIsDisabled}
                                                    data-testid={'checkbox-' + chargePoint.id}
                                                />
                                            </SCheckboxLabel>
                                        </STableCell>
                                        <STableCell>
                                            <Link
                                                to={`/chargepoints/${chargePoint.id}`}
                                                className="btn-link"
                                                data-testid={'id-' + chargePoint.id}
                                            >
                                                {chargePoint.id}
                                            </Link>
                                        </STableCell>
                                        <STableCell data-testid={'name-' + chargePoint.id}>
                                            {chargePoint.name}
                                        </STableCell>
                                        <STableCell data-testid={'status-' + chargePoint.id}>
                                            <SStatusIcon status={getStatus(chargePoint)} />
                                        </STableCell>
                                        <STableCell data-testid={'org-' + chargePoint.id}>
                                            {chargePoint.companyName}
                                        </STableCell>
                                        <STableCell data-testid={'fw-version-' + chargePoint.id}>
                                            {chargePoint.isOcpp
                                                ? getFirmwareVersion(chargePoint.ocppConfigKeys)
                                                : chargePoint.installedFirmwareVersion}
                                        </STableCell>
                                        <STableCell data-testid={'available-' + chargePoint.id}>
                                            {chargePoint?.availableFirmwareVersion}
                                        </STableCell>
                                        <STableCell data-testid={'fw-status-' + chargePoint.id}>
                                            <FWStatusIcon
                                                availableVersion={chargePoint.availableFirmwareVersion}
                                                currentVersion={chargePoint.installedFirmwareVersion}
                                            />
                                        </STableCell>
                                        <STableCell data-testid={'last-updated-' + chargePoint.id}>
                                            {displayDateTime(chargePoint?.firmwareLastUpdated)}
                                        </STableCell>
                                    </STableRow>
                                )
                            })}
                        </tbody>
                    </STable>
                </STableWrapper>
            )}
            {totalCount >= pageSize && !isPending && (
                <ChargersPagination
                    setPageIndex={handlePageClick}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    pageIndex={currentPage}
                    displayedChargers={pageSize}
                />
            )}
        </WidgetWrapper>
    )
}

export default UpdateChargersFirmwareList
